import type { LucideIcon } from 'lucide-react';
import { Badge } from '@ooh/ui/components/badge';
import { IconTooltipBadge } from '@ooh/ui/components/icon-tooltip-badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ooh/ui/components/table';
import type { AvailabilityWindowState } from '@ooh/contracts';
import { availabilityWindowStateVariantMap } from '../../lib/availability-window-state';

export interface InterpretedRow {
  id: string;
  /** Our unit code when matched, or the raw supplier-file value when unmatched. */
  unit: string;
  matched: boolean;
  /** null while the operator hasn't supplied enough mapping info to derive a period yet. */
  period: string | null;
  status: AvailabilityWindowState | null;
}

export interface InterpretedRowsTableLabels {
  unitColumnLabel: string;
  periodColumnLabel: string;
  statusColumnLabel: string;
  /** Template with a {unit} placeholder, e.g. "Niedopasowany ({unit})". */
  unmatchedLabelTemplate: string;
  unmatchedTooltip: string;
  emptyPlaceholder: string;
  /** Translated display text per status value, e.g. { FREE: 'Wolne', UNAVAILABLE: 'Niedostępne' }. */
  statusLabels: Record<AvailabilityWindowState, string>;
}

function formatUnmatchedLabel(template: string, unit: string): string {
  return template.replace('{unit}', unit);
}

function StatusBadge({
  status,
  emptyPlaceholder,
  statusLabels,
}: {
  status: AvailabilityWindowState | null;
  emptyPlaceholder: string;
  statusLabels: Record<AvailabilityWindowState, string>;
}) {
  if (status === null) return <span className="text-muted-foreground">{emptyPlaceholder}</span>;
  return <Badge variant={availabilityWindowStateVariantMap[status]}>{statusLabels[status]}</Badge>;
}

export interface InterpretedRowsTableProps {
  rows: InterpretedRow[];
  labels: InterpretedRowsTableLabels;
  unmatchedIcon: LucideIcon;
  testId?: string;
}

/**
 * Preview of the RESULT of the current resolved profile: one row per detected
 * (unit, period, state) fact. Read-only — this table previews the outcome of
 * the mapping fields above it; fixes belong in those fields, not here.
 */
export function InterpretedRowsTable({
  rows,
  labels,
  unmatchedIcon: UnmatchedIcon,
  testId = 'interpreted-rows-table',
}: InterpretedRowsTableProps) {
  return (
    <div className="rounded-lg border border-border overflow-hidden" data-testid={testId}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{labels.unitColumnLabel}</TableHead>
            <TableHead>{labels.periodColumnLabel}</TableHead>
            <TableHead>{labels.statusColumnLabel}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} listRow data-testid={`${testId}-row-${row.id}`}>
              <TableCell className="px-4 py-3 font-mono text-[13px]">
                {row.matched ? (
                  row.unit
                ) : (
                  <IconTooltipBadge
                    tone="warning"
                    icon={UnmatchedIcon}
                    label={formatUnmatchedLabel(labels.unmatchedLabelTemplate, row.unit)}
                    text={labels.unmatchedTooltip}
                    testId={`${testId}-row-${row.id}-unmatched`}
                  />
                )}
              </TableCell>
              <TableCell className="px-4 py-3 text-[13px] text-muted-foreground">
                {row.period ?? labels.emptyPlaceholder}
              </TableCell>
              <TableCell className="px-4 py-3">
                <StatusBadge
                  status={row.status}
                  emptyPlaceholder={labels.emptyPlaceholder}
                  statusLabels={labels.statusLabels}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
